import { WarningCircle } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { FieldError as AriaFieldError, Label as AriaLabel, Text } from "react-aria-components";
import { cx } from "@/lib/cx";

/** Shared pieces for every form control: label above, optional description, error below. */

export const fieldRoot = "flex min-w-0 flex-col gap-(--gap-field)";

/** Inputs, select triggers and comboboxes share one shape: 32px (compact), hairline 3:1 border, inset feel. */
export const inputBase =
  "w-full min-w-0 rounded-control border border-input-border bg-input-bg px-2.5 text-sm text-fg " +
  "shadow-[0_1px_2px_var(--shadow-soft)] transition-ui placeholder:text-fg-subtle " +
  "data-hovered:border-input-border-hover data-invalid:border-input-border-invalid " +
  "data-disabled:opacity-50 data-disabled:cursor-not-allowed";

export const controlHeight = "h-(--input-h)";

export function FieldLabel({
  children,
  hidden,
  required,
  className,
}: {
  children: ReactNode;
  hidden?: boolean;
  /** Adds a visible "*" (assistive tech gets `aria-required` from the field itself, so the mark is hidden from it). */
  required?: boolean;
  className?: string;
}) {
  return (
    <AriaLabel className={cx(hidden ? "sr-only" : "text-sm font-medium text-fg", className)}>
      {children}
      {required && !hidden ? (
        <span aria-hidden="true" className="ml-0.5 text-fg-muted">
          *
        </span>
      ) : null}
    </AriaLabel>
  );
}

export function FieldDescription({ children }: { children: ReactNode }) {
  return (
    <Text slot="description" className="text-xs text-fg-muted">
      {children}
    </Text>
  );
}

/** Renders only while the field is invalid. Icon + text, so it is never colour-only. */
export function FieldErrorText({ children }: { children?: ReactNode }) {
  return (
    <AriaFieldError className="flex items-start gap-1.5 text-xs text-danger-text">
      {({ validationErrors }) => (
        <>
          <WarningCircle size={14} weight="fill" aria-hidden="true" className="mt-px shrink-0" />
          <span className="min-w-0">{children ?? validationErrors.join(" ")}</span>
        </>
      )}
    </AriaFieldError>
  );
}

export interface BaseFieldProps {
  label: string;
  /** Visually hide the label but keep it for assistive tech (rare: search boxes). */
  labelHidden?: boolean;
  description?: ReactNode;
  /** Controlled error message shown under the field. */
  error?: string;
}
